// MemberRankings.js
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase"; // adjust path if needed
import "../styles/AppStyles.css";
import "../styles/MemberRanking.css";
import { Link } from "react-router-dom";

/**
 * MemberRankings component (show all transactions per member)
 * - aggregates referral transactions per referrer
 * - shows an inline expandable transaction list per referrer
 * - export buttons unchanged
 */

const MemberRankings = () => {
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topN, setTopN] = useState(25); // how many to show
  const [expanded, setExpanded] = useState({}); // map referrerId -> boolean

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const usersRef = collection(db, "users");
        const usersSnap = await getDocs(usersRef);
        const usersData = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(usersData);

        const txRef = collection(db, "transactions");
        const txQuery = query(txRef, orderBy("createdAt", "desc"));
        const txSnap = await getDocs(txQuery);
        const txData = txSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTransactions(txData);
      } catch (err) {
        console.error("MemberRankings fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // helpers
  const safeValue = (v) => {
    if (v === null || typeof v === "undefined") return "";
    if (typeof v === "object") {
      if (typeof v.toDate === "function") {
        try {
          return v.toDate().toISOString();
        } catch {
          return String(v);
        }
      }
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  };

  const currencyFormat = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  // Build ranking but also aggregate transactions per referrer
  const buildRanking = () => {
    const map = {};
    const userById = {};
    users.forEach((u) => (userById[u.bdaId] = u));

    transactions.forEach((t) => {
      const isReferral =
        (t.type && typeof t.type === "string" && t.type.toLowerCase() === "referral") ||
        (t.referral && Boolean(t.referral)) ||
        (t.description && String(t.description).toLowerCase().includes("referral"));

      if (!isReferral) return;

      const referrerId = t.userId;
      if (!referrerId) return;

      if (!map[referrerId]) {
        const u = userById[referrerId] || {};
        map[referrerId] = {
          userId: referrerId,
          bdaId: u.bdaId || referrerId,
          name: u.displayName || u.name || "Unknown",
          city: u.city || u.location || "",
          referrals: 0,
          incentiveSum: 0,
          recentDescription: "",
          transactions: [], // collect all referral transactions for this referrer
        };
      }

      map[referrerId].referrals += 1;
      const amount = Number(t.amount || 0);
      map[referrerId].incentiveSum += isNaN(amount) ? 0 : amount;

      // Because transactions are fetched with createdAt desc,
      // the first description set becomes the most recent
      if (!map[referrerId].recentDescription && t.description) {
        map[referrerId].recentDescription = t.description;
      }

      // push transaction details (keep relevant fields)
      map[referrerId].transactions.push({
        id: t.id,
        createdAt: t.createdAt || t.timestamp || null,
        amount: t.amount || 0,
        status: t.status || "",
        description: t.description || "",
        referredUserId: t.referredUserId || t.referred_user_id || "",
        metadata: t.metadata || null,
        raw: t,
      });
    });

    // Convert to array and sort
    const arr = Object.values(map).sort((a, b) => {
      if (b.referrals !== a.referrals) return b.referrals - a.referrals;
      return b.incentiveSum - a.incentiveSum;
    });

    return arr;
  };

  const ranking = buildRanking();
  const displayed = ranking.slice(0, topN);

  // ---------- CSV export helpers (unchanged) ----------
  const toCsv = (rows) => {
    if (!rows || rows.length === 0) return "";
    const fields = Object.keys(rows[0]);
    const escapeCell = (s) => `"${String(s).replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(fields.join(","));
    rows.forEach((r) => {
      const row = fields.map((f) => escapeCell(safeValue(r[f] ?? ""))).join(",");
      lines.push(row);
    });
    return lines.join("\n");
  };

  const triggerDownload = (content, filename = "export.csv") => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportTransactions = () => {
    const rows = transactions.map((t) => ({
      id: t.id || "",
      createdAt: safeValue(t.createdAt || t.timestamp || ""),
      type: t.type || "",
      userId: t.userId || "",
      referredUserId: t.referredUserId || t.referred_user_id || "",
      amount: t.amount || "",
      status: t.status || "",
      description: t.description || "",
      metadata: t.metadata ? JSON.stringify(t.metadata) : "",
    }));

    if (rows.length === 0) {
      alert("No transactions to export.");
      return;
    }

    const csv = toCsv(rows);
    triggerDownload(csv, `transactions_export_${Date.now()}.csv`);
  };

  const handleExportRanking = () => {
    const rows = displayed.map((r, idx) => ({
      rank: idx + 1,
      bdaId: r.bdaId,
      name: r.name,
      city: r.city,
      referrals: r.referrals,
      totalIncentive: r.incentiveSum,
      // Include all descriptions concatenated for export convenience
      allDescriptions:
        (r.transactions && r.transactions.map((t) => t.description || "").filter(Boolean).join(" ; ")) || "",
    }));
    if (rows.length === 0) {
      alert("No ranking rows to export.");
      return;
    }
    const csv = toCsv(rows);
    triggerDownload(csv, `rankings_export_${Date.now()}.csv`);
  };

  // toggle expand/collapse for a referrer
  const toggleExpand = (referrerId) => {
    setExpanded((p) => ({ ...p, [referrerId]: !p[referrerId] }));
  };

  if (loading) return <div className="loading">Loading rankings.</div>;

  return (
    <div className="member-rankings admin-home-like">
      <div className="page-header">
        <h2>Member Rankings</h2>
        <div className="header-actions">
          <Link to="/admin" className="admin-button small btnSpace">
            Back to Admin
          </Link>
          <Link to="/admin/members" className="admin-button small">
            Manage Members
          </Link>

          <button
            onClick={handleExportTransactions}
            className="admin-button small btnSpace"
            title="Export all transactions (CSV) including description"
            style={{ marginLeft: 8 }}
          >
            Export Transactions (CSV)
          </button>

          <button
            onClick={handleExportRanking}
            className="admin-button small"
            title="Export ranking table (CSV)"
            style={{ marginLeft: 6 }}
          >
            Export Ranking (CSV)
          </button>
        </div>
      </div>

      <div className="rank-stats">
        <div className="stat-card small">
          <h4>Total Referrers</h4>
          <p>{ranking.length}</p>
        </div>
        <div className="stat-card small">
          <h4>Total Referral Count</h4>
          <p>{ranking.reduce((s, r) => s + r.referrals, 0)}</p>
        </div>
        <div className="stat-card small">
          <h4>Total Incentives</h4>
          <p>{currencyFormat(ranking.reduce((s, r) => s + r.incentiveSum, 0))}</p>
        </div>
      </div>

      <div className="ranking-controls">
        <label>
          Show top
          <select
            value={topN}
            onChange={(e) => setTopN(Number(e.target.value))}
            style={{ marginLeft: 8 }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="ranking-table-wrap">
        <table className="ranking-table">
          <thead>
            <tr>
              <th>#</th>
              <th>BDA ID</th>
              <th>Member Name</th>
              <th>City</th>
              <th>No. of Referrals</th>
              <th>Total Incentive</th>
              <th>Transactions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: 20 }}>
                  No referral transactions found.
                </td>
              </tr>
            )}

            {displayed.map((r, idx) => {
              const rank = idx + 1;
              const rankClass =
                rank === 1 ? "gold" : rank === 2 ? "silver" : rank === 3 ? "bronze" : "";
              const isExpanded = Boolean(expanded[r.userId]);

              return (
                <React.Fragment key={r.userId}>
                  <tr className={rankClass}>
                    <td className="rank-cell">{rank}</td>
                    <td>{r.bdaId}</td>
                    <td>{r.name}</td>
                    <td>{r.city}</td>
                    <td>{r.referrals}</td>
                    <td>{currencyFormat(r.incentiveSum)}</td>
                    <td>
                      <button onClick={() => toggleExpand(r.userId)} className="small">
                        {isExpanded ? `Hide (${r.transactions.length})` : `View (${r.transactions.length})`}
                      </button>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={7} style={{ padding: 8, background: "#fff" }}>
                        <div style={{ maxHeight: 240, overflow: "auto", border: "1px solid #eee", borderRadius: 6 }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead style={{ background: "#f7f7f7" }}>
                              <tr>
                                <th style={{ padding: 8, textAlign: "left" }}>Date</th>
                                <th style={{ padding: 8, textAlign: "left" }}>Txn ID</th>
                                <th style={{ padding: 8, textAlign: "right" }}>Amount</th>
                                <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                                <th style={{ padding: 8, textAlign: "left" }}>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.transactions.map((t) => (
                                <tr key={t.id} style={{ borderTop: "1px solid #fafafa" }}>
                                  <td style={{ padding: 8, verticalAlign: "top", width: 180 }}>
                                    {t.createdAt ? (typeof t.createdAt.toDate === "function" ? t.createdAt.toDate().toLocaleString() : String(t.createdAt)) : "-"}
                                  </td>
                                  <td style={{ padding: 8, verticalAlign: "top", width: 220, wordBreak: "break-all" }}>{t.id}</td>
                                  <td style={{ padding: 8, verticalAlign: "top", textAlign: "right", width: 120 }}>{currencyFormat(t.amount)}</td>
                                  <td style={{ padding: 8, verticalAlign: "top", width: 120 }}>{t.status || "-"}</td>
                                  <td style={{ padding: 8, verticalAlign: "top", whiteSpace: "normal", wordBreak: "break-word" }}>
                                    {t.description || "-"}
                                  </td>
                                </tr>
                              ))}

                              {r.transactions.length === 0 && (
                                <tr>
                                  <td colSpan={5} style={{ padding: 12, textAlign: "center" }}>
                                    No transactions recorded.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12 }}>
        <small className="hint">
          Rankings are built from transactions with type "Referral". Click "View" to see all transactions for a referrer.
        </small>
      </div>
    </div>
  );
};

export default MemberRankings;
